import React, { useState, useEffect, ChangeEvent, useRef } from 'react';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import HeadingBlock from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import { AnalyticsService, CompanyService, TranscriptionsAdminService } from '$/services';
import { downloadAdminTranscriptionAudio, downloadAdminTranscriptionText, downloadFile } from '$/store/export/actions';
import { toastError, toastSuccess } from '$/store/toast/actions';
import './index.scss';
import Modal from '$/components/global/modal';
import InputBlock from '$/components/global/inputBlock';
import BixButton from '$/components/global/bixButton';
import dayjs from 'dayjs';
import { Field, ErrorMessage, Formik, FormikProps } from 'formik';
import BixbyQuillWrapper from '$/components/quill';
import BixCheckbox from '$/components/global/bixCheckbox';
import classnames from 'classnames';
import { Navigate, useMatch } from 'react-router-dom';

interface IProps {
  profile?: Bixby.Models.Profile;
  dispatchDownloadTranscriptAudio: (
    id: string,
    bixbyId: string,
    container: Bixby.Enums.TranscriptionContainer
  ) => Promise<void>;
  dispatchDownloadTranscriptText: (
    id: string,
    bixbyId: string,
    container: Bixby.Enums.TranscriptionContainer
  ) => Promise<void>;
  downloadFile: (blob: Blob, bixbyId: string, fileExtension: string) => Promise<void>;
  dispatchToastSuccess: (isDraft: boolean) => Promise<void>;
  dispatchToastFailure: (isDraft: boolean) => Promise<void>;
}

interface FormValues {
  entry: string;
  sendCompanyEmail: boolean;
  uploadedTranscriptText?: File;
  subject: string;
  extraConferSteps: {
    relatedText: boolean;
    conferTranscriptAudio: boolean;
    conferTranscriptText: boolean;
    publishFinal: boolean;
  };
}

enum ModalType {
  ConfirmDraft,
  ConfirmFinal,
}

const AdminTranscriptPublish: React.FC<IProps> = props => {
  const {
    profile,
    dispatchDownloadTranscriptAudio,
    dispatchDownloadTranscriptText,
    downloadFile,
    dispatchToastSuccess,
    dispatchToastFailure,
  } = props;

  const match = useMatch('/admin/send-company-update/:companyId/transcript/:transcriptId');
  const [isLoading, setIsLoading] = useState(true);
  const [company, setCompany] = useState(undefined as Bixby.Models.AdminCompanyNews | undefined);
  const [initialValues, setInitialValues] = useState<FormValues | undefined>(undefined);
  const [validationSchema, setValidationSchema] = useState<object>();
  const [transcript, setTranscript] = useState(undefined as Bixby.Models.AdminCompanyTranscription | undefined);
  const [publishSuccessful, setPublishSuccessful] = useState(false);
  const [uploadTranscriptTextModal, setUploadTranscriptTextModal] = useState(false);
  const [transcriptUploadError, setTranscriptUploadError] = useState(undefined as string | undefined);
  const [transcriptUploadSuccess, setTranscriptUploadSuccess] = useState(false);
  const [transcriptUploading, setTranscriptUploading] = useState(false);
  const [downloadingAudio, setDownloadingAudio] = useState(false);
  const [downloadedAudio, setDownloadedAudio] = useState(false);
  const [downloadingText, setDownloadingText] = useState(false);
  const [downloadedText, setDownloadedText] = useState(false);
  const [modalType, setModalType] = useState<ModalType | undefined>(undefined);
  const [publishing, setPublishing] = useState(false);
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const handleDownloadTranscriptAudio = async () => {
    if (!transcript?.transcriptionId) return;
    setDownloadingAudio(true);
    handleTranscriptionEvent(
      Bixby.Constants.APPINSIGHTS_TRANSCRIPTIONS_ADMIN_REVIEW_AUDIO_DOWNLOAD,
      transcript.transcriptionId
    );
    const container =
      transcript?.transcriptionAudioFileState === Bixby.Enums.TranscriptionFileState.Published
        ? Bixby.Enums.TranscriptionContainer.Published
        : Bixby.Enums.TranscriptionContainer.Staged;
    await dispatchDownloadTranscriptAudio(transcript?.transcriptionId, transcript?.companyBixbyId, container);
    setDownloadingAudio(false);
    setDownloadedAudio(true);
  };

  const handleDownloadTranscriptText = async (blob?: Blob) => {
    if (!transcript?.transcriptionId) return;
    setDownloadingText(true);
    handleTranscriptionEvent(
      Bixby.Constants.APPINSIGHTS_TRANSCRIPTIONS_ADMIN_REVIEW_TEXT_DOWNLOAD,
      transcript?.transcriptionId
    );
    if (blob) {
      await downloadFile(blob, transcript?.companyBixbyId, 'docx');
    } else {
      const container =
        transcript?.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.Published
          ? Bixby.Enums.TranscriptionContainer.Published
          : Bixby.Enums.TranscriptionContainer.Staged;
      await dispatchDownloadTranscriptText(transcript?.transcriptionId, transcript?.companyBixbyId, container);
    }
    setDownloadingText(false);
    setDownloadedText(true);
  };

  const handleTranscriptionEvent = (eventName: string, transcriptionId: string) => {
    if (profile) {
      AnalyticsService.event(eventName, {
        ...profile,
        companyId: company?.id,
        companyName: company?.name,
        transcriptionId: transcriptionId,
      });
    }
  };

  useEffect(() => {
    if (!match?.params) return;

    const getCompany = async () => {
      if (!match?.params.companyId) return;
      try {
        const company: Bixby.Models.AdminCompanyNews = await CompanyService.getForNews(match.params.companyId);
        setCompany(company);
      } catch (err: any) {
        setIsLoading(false);
      }
    };

    const getTranscript = async () => {
      if (!match?.params.transcriptId) return;
      try {
        const transcript: Bixby.Models.AdminCompanyTranscription = await TranscriptionsAdminService.getById(
          match.params.transcriptId
        );
        setTranscript(transcript);
      } catch (err: any) {
        setIsLoading(false);
      }
    };

    getTranscript();
    getCompany();
  }, [match]);

  useEffect(() => {
    if (!transcript || !company) return;

    const initialFormValues: FormValues = {
      entry: `A call transcript is now available for ${transcript.companyName}. To access the transcript and audio file, please click the link below.`,
      sendCompanyEmail: false,
      uploadedTranscriptText: undefined,
      subject: `${transcript.companyName} - Call Transcript Available`,
      extraConferSteps: {
        relatedText: false,
        conferTranscriptAudio: false,
        conferTranscriptText: false,
        publishFinal: false,
      },
    };

    // validation schema
    const formSchema = Yup.object({
      extraConferSteps: Yup.object({
        relatedText: Yup.boolean().oneOf([true], 'Please verify text relates to company.'),
        conferTranscriptAudio: Yup.boolean().oneOf(
          [true],
          `You must confer the transcript audio matches to ${transcript.companyName}`
        ),
        conferTranscriptText: Yup.boolean().oneOf(
          [true],
          `You must confer the transcript text matches to ${transcript.companyName}`
        ),
      }),
    });
    setInitialValues(initialFormValues);
    setValidationSchema(formSchema);

    setIsLoading(false);
  }, [transcript, company]);

  const handleTranscriptUpload = async (event: ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    const file: File | undefined = (event.target.files || [])[0];
    if (!file) {
      setTranscriptUploadError('File is required');
      return;
    } else if (file.name !== `${transcript?.companyBixbyId}.docx`) {
      setTranscriptUploadError('Document name must be [BIXBY_ID].docx');
      return;
    }

    setTranscriptUploadError(undefined);
    setTranscriptUploading(true);
    setTranscriptUploadSuccess(false);

    // setTimeout for visual effect
    setTimeout(() => {
      setFieldValue('uploadedTranscriptText', file);
      setTranscriptUploadSuccess(true);
      setDownloadedText(true);

      setTranscriptUploading(false);
    }, 500);
  };

  const handleTranscriptUploadModalClose = () => {
    setUploadTranscriptTextModal(false);
    // setTimeout so we don't display flash of reset content
    setTimeout(() => {
      setTranscriptUploadError(undefined);
      setTranscriptUploading(false);
      setTranscriptUploadSuccess(false);
    });
  };

  const handleSubmit = async (values: FormValues) => {
    const isDraft = modalType === ModalType.ConfirmDraft;
    setPublishing(true);
    try {
      const model = {
        transcriptionId: transcript?.transcriptionId,
        entry: values.entry,
        subject: values.subject,
        sendEmail: values.sendCompanyEmail,
      } as Bixby.Models.PublishTranscriptionRequest;

      if (isDraft) {
        await TranscriptionsAdminService.publishDraftTranscription(model, values.uploadedTranscriptText);
      } else {
        await TranscriptionsAdminService.publishLiveTranscription(model, values.uploadedTranscriptText);
      }

      dispatchToastSuccess(isDraft);
      setPublishSuccessful(true);
    } catch (e: any) {
      dispatchToastFailure(isDraft);
      setPublishSuccessful(false);
    } finally {
      setModalType(undefined);
      setPublishing(false);
    }
  };

  const unstagedFileStates = [
    Bixby.Enums.TranscriptionFileState.NotSubmitted,
    Bixby.Enums.TranscriptionFileState.Submitted,
    Bixby.Enums.TranscriptionFileState.Processing,
  ];

  if (publishSuccessful) {
    return <Navigate to="/admin/transcripts" />;
  }

  if (isLoading) {
    return <Loading pageLevel />;
  } else if (Bixby.Utils.isNullOrEmpty(transcript)) {
    return <NoDataFound dataType="transcript" />;
  } else if (Bixby.Utils.isNullOrEmpty(company)) {
    return <NoDataFound dataType="company" />;
  } else if (
    transcript &&
    transcript.transcriptionStatus !== Bixby.Enums.TranscriptionStatus.Draft &&
    Bixby.Utils.isDefined(transcript.transcriptionPublishedDate) &&
    transcript.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.Published &&
    transcript.transcriptionAudioFileState === Bixby.Enums.TranscriptionFileState.Published
  ) {
    // reject if transcript has already been published
    return <p className="publish_transcript">This transcript has already been published.</p>;
  } else if (
    transcript &&
    (unstagedFileStates.includes(transcript.transcriptionTextFileState) ||
      unstagedFileStates.includes(transcript.transcriptionAudioFileState))
  ) {
    // reject if transcript has already been published
    return (
      <p className="publish_transcript">
        This audio file is{' '}
        {transcript.transcriptionAudioFileState === Bixby.Enums.TranscriptionFileState.NotSubmitted
          ? 'not submitted'
          : 'processing'}{' '}
        and the transcript file is{' '}
        {transcript.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.NotSubmitted
          ? 'not submitted'
          : 'processing'}
        . Please check back again later.
      </p>
    );
  } else {
    // draft transcript but transcript has published fields OR transcript is still staged

    return (
      <React.Fragment>
        <HeadingBlock title={'Publish Transcript'} />
        <section className="company_update_page publish_transcript">
          <Formik
            innerRef={formikRef}
            initialValues={initialValues || ({} as FormValues)}
            validationSchema={validationSchema}
            onSubmit={(values: FormValues) => {
              handleSubmit(values);
            }}
            render={({ values, setFieldValue, isValid }) =>
              transcript && (
                <form>
                  <div className="grid">
                    <div>
                      <p className="label">Bixby ID</p>
                      <p>{transcript.companyBixbyId}</p>
                    </div>
                    <div>
                      <p className="label">Subject</p>
                      <p>{values.subject}</p>
                    </div>
                    <div className="full_span">
                      <p className="label">Timezone</p>
                      <p>{Bixby.Constants.TIMEZONES.find(_ => _.Id === transcript.newsLenderTimezone)?.DisplayName}</p>
                    </div>
                    <div>
                      <p className="label">Stard Date & Time</p>
                      <p>{dayjs(transcript.newsLenderStartDate).format('MMMM D, YYYY h:mm A')}</p>
                    </div>
                    <div>
                      <p className="label">End Date & Time</p>
                      <p>{dayjs(transcript.newsLenderEndDate).format('MMMM D, YYYY h:mm A')}</p>
                    </div>
                    <div>
                      <p className="label">Replay Good From</p>
                      <p>{dayjs(transcript.transcriptionReplayGoodFrom).format('MMMM D, YYYY h:mm A')}</p>
                    </div>
                    <div className="inner_grid">
                      <div>
                        <p className="label">Days Until Expiration</p>
                        <p>
                          {dayjs(transcript.transcriptionReplayGoodUntil).diff(
                            transcript.transcriptionReplayGoodFrom,
                            'days'
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="label">Replay Good Until</p>
                        <p>{dayjs(transcript.transcriptionReplayGoodUntil).format('MMMM D, YYYY h:mm A')}</p>
                      </div>
                    </div>
                    <div>
                      <p className="label">Audio File Status</p>
                      <p
                        className={classnames({
                          published:
                            transcript?.transcriptionAudioFileState === Bixby.Enums.TranscriptionFileState.Published,
                        })}>
                        {transcript.transcriptionAudioFileState}
                      </p>
                    </div>
                    <div>
                      <p className="label">Transcript File Status</p>
                      <p
                        className={classnames({
                          published:
                            transcript?.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.Published &&
                            transcript?.transcriptionStatus === Bixby.Enums.TranscriptionStatus.Live,
                          published_draft:
                            transcript?.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.Published &&
                            transcript?.transcriptionStatus === Bixby.Enums.TranscriptionStatus.Draft,
                        })}>
                        {transcript.transcriptionTextFileState}
                        {transcript.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.Published &&
                          transcript.transcriptionStatus === Bixby.Enums.TranscriptionStatus.Draft &&
                          ' - DRAFT'}
                        {transcript.transcriptionTextFileState === Bixby.Enums.TranscriptionFileState.Published &&
                          transcript.transcriptionStatus === Bixby.Enums.TranscriptionStatus.Live &&
                          ' - FINAL'}
                      </p>
                    </div>
                    <div>
                      <Field
                        name="sendCompanyEmail"
                        render={({ field }: { [key: string]: any }) => (
                          <BixCheckbox
                            id={field.name}
                            checked={field.value}
                            onChange={event => {
                              const target = event.target as HTMLInputElement;
                              setFieldValue(field.name, target.checked);
                            }}>
                            Send Company Update Email
                          </BixCheckbox>
                        )}
                      />
                    </div>
                    <div className="full_span">
                      <div dangerouslySetInnerHTML={{ __html: transcript.newsLenderCallEntry }}></div>
                    </div>
                    <div className="full_span">
                      <InputBlock title="Mail Body">
                        <Field
                          name="entry"
                          render={({ field, form }: { [key: string]: any }) => {
                            const handleMailBodyOnBlur = () => {
                              form.setFieldTouched('entry');
                            };
                            return (
                              <BixbyQuillWrapper
                                value={field.value}
                                onBlur={handleMailBodyOnBlur}
                                onChange={field.onChange(field.name)}
                              />
                            );
                          }}
                        />
                      </InputBlock>
                      <ErrorMessage name="entry">
                        {msg => <div className="error error-message">{msg}</div>}
                      </ErrorMessage>
                    </div>
                  </div>
                  <div>
                    <Field
                      name="extraConferSteps.relatedText"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id={field.name}
                          checked={field.value}
                          onChange={event => {
                            const target = event.target as HTMLInputElement;
                            setFieldValue(field.name, target.checked);
                          }}>
                          {`I confer all text relates to ${transcript.companyName}`}
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name="extraConferSteps.conferTranscriptAudio">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </div>
                  <div>
                    <Field
                      name="extraConferSteps.conferTranscriptAudio"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id={field.name}
                          checked={field.value}
                          onChange={event => {
                            const target = event.target as HTMLInputElement;
                            setFieldValue(field.name, target.checked);
                          }}
                          disabled={!downloadedAudio}
                          postLabelRender={
                            <div className="loading_contain">
                              <div role="button" className="fake_link" onClick={handleDownloadTranscriptAudio}>
                                Download transcript audio
                              </div>
                              {downloadingAudio && <Loading inline />}
                            </div>
                          }>
                          {`I confer the transcript audio relates to ${transcript.companyName}`}
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name="extraConferSteps.conferTranscriptAudio">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </div>
                  <div>
                    <Field
                      name="extraConferSteps.conferTranscriptText"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id={field.name}
                          checked={field.value}
                          onChange={event => {
                            const target = event.target as HTMLInputElement;
                            setFieldValue(field.name, target.checked);
                          }}
                          disabled={!downloadedText}
                          postLabelRender={
                            <>
                              {values.uploadedTranscriptText && (
                                <div className="remove_file_contain">
                                  <div
                                    role="button"
                                    className="fake_link"
                                    onClick={() => handleDownloadTranscriptText(values.uploadedTranscriptText)}>
                                    {values.uploadedTranscriptText.name}
                                  </div>
                                  <div
                                    role="button"
                                    className="remove_file"
                                    onClick={() => setFieldValue('uploadedTranscriptText', undefined)}></div>
                                </div>
                              )}
                              {!values.uploadedTranscriptText && (
                                <div className="download_confer_contain">
                                  <div className="loading_contain">
                                    <div
                                      role="button"
                                      className="fake_link"
                                      onClick={() => handleDownloadTranscriptText()}>
                                      Download transcript text
                                    </div>
                                    {downloadingText && <Loading inline />}
                                  </div>
                                  <>
                                    <span>&nbsp;or&nbsp;</span>
                                    <div
                                      role="button"
                                      className="fake_link"
                                      onClick={() => setUploadTranscriptTextModal(true)}>
                                      re-upload transcript text
                                    </div>
                                  </>
                                </div>
                              )}
                            </>
                          }>
                          {`I confer the transcript text relates to ${transcript.companyName}`}
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name="extraConferSteps.conferTranscriptText">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </div>
                  <div>
                    <BixButton light disabled={!isValid} onClick={() => setModalType(ModalType.ConfirmDraft)}>
                      Publish Draft
                    </BixButton>
                  </div>
                  <div className="final_confer">
                    <Field
                      name="extraConferSteps.publishFinal"
                      render={({ field }: { [key: string]: any }) => (
                        <BixCheckbox
                          id={field.name}
                          checked={field.value}
                          onChange={event => {
                            const target = event.target as HTMLInputElement;
                            setFieldValue(field.name, target.checked);
                          }}>
                          Review complete, ready to publish FINAL
                        </BixCheckbox>
                      )}
                    />
                    <ErrorMessage name="extraConferSteps.conferTranscriptAudio">
                      {msg => <div className="error error-message">{msg}</div>}
                    </ErrorMessage>
                  </div>
                  <BixButton
                    disabled={!isValid || !values.extraConferSteps.publishFinal}
                    onClick={() => setModalType(ModalType.ConfirmFinal)}>
                    Publish Final
                  </BixButton>
                  {transcript && (
                    <>
                      <Modal active={uploadTranscriptTextModal} onClose={handleTranscriptUploadModalClose}>
                        <div className="reupload_modal_content">
                          {transcriptUploading && <Loading size={10} />}
                          {!transcriptUploading && (
                            <>
                              {!transcriptUploadSuccess && (
                                <>
                                  <p>Re-Upload transcript for {transcript.companyName}</p>
                                  <InputBlock>
                                    <input
                                      id="transcriptFileUpload"
                                      name="transcriptFileUpload"
                                      accept=".docx, application/msword"
                                      type="file"
                                      onChange={e => handleTranscriptUpload(e, setFieldValue)}
                                      multiple={false}
                                    />
                                  </InputBlock>
                                  {transcriptUploadError && <p className="error_msg">{transcriptUploadError}</p>}
                                </>
                              )}
                              {transcriptUploadSuccess && (
                                <>
                                  <p>Updated transcript for {transcript.companyName}</p>
                                  <BixButton onClick={handleTranscriptUploadModalClose}>Close</BixButton>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </Modal>
                      <Modal
                        active={Bixby.Utils.isDefined(modalType)}
                        title={`Publish transcript as ${
                          modalType === ModalType.ConfirmDraft ? 'draft' : 'final'
                        } version`}
                        onClose={() => (publishing ? void 0 : setModalType(undefined))}>
                        <div>
                          {publishing && <Loading size={10} />}
                          {!publishing && (
                            <div className="modal_button_container">
                              <BixButton light type="button" onClick={() => setModalType(undefined)}>
                                Cancel
                              </BixButton>
                              <BixButton type="button" onClick={() => handleSubmit(values)}>
                                Confirm
                              </BixButton>
                            </div>
                          )}
                        </div>
                      </Modal>
                    </>
                  )}
                </form>
              )
            }
          />
          <hr />
        </section>
      </React.Fragment>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  return {
    profile: state.profileState.profile,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadTranscriptAudio: (id: string, bixbyId: string, container: Bixby.Enums.TranscriptionContainer) =>
    dispatch(downloadAdminTranscriptionAudio(id, bixbyId, container)),
  dispatchDownloadTranscriptText: (id: string, bixbyId: string, container: Bixby.Enums.TranscriptionContainer) =>
    dispatch(downloadAdminTranscriptionText(id, bixbyId, container)),
  downloadFile: (blob: Blob, bixbyId: string, fileExtension: string) =>
    dispatch(downloadFile(blob, bixbyId, fileExtension, true)),
  dispatchToastSuccess: (isDraft: boolean) => dispatch(toastSuccess(`Transcript${isDraft ? ' draft' : ''} published`)),
  dispatchToastFailure: (isDraft: boolean) =>
    dispatch(toastError(`Failed to publish transcript${isDraft ? ' draft' : ''}`)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminTranscriptPublish);
